import React, { useState, useEffect } from 'react';
import Modal from '@components/ui/Modal';
import { RiCalendarLine } from '@remixicon/react';

/**
 * Returns today's date in local time as YYYY-MM-DD (the format <input type="date"> expects).
 * Using local components avoids the off-by-one timezone bug from `toISOString().split('T')[0]`.
 */
const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * QuoteSearchByDateModal
 * Lets the user pick a single date. Defaults to today.
 * On confirm, calls onSelect(isoDate, label).
 */
const QuoteSearchByDateModal = ({ isOpen, onClose, onSelect, initialDate }) => {
  const [date, setDate] = useState(initialDate || todayIso());

  useEffect(() => {
    if (isOpen) setDate(initialDate || todayIso());
  }, [isOpen, initialDate]);

  const handleConfirm = () => {
    if (!date) return;
    // Format label as "MMM D, YYYY" using the user's locale
    const d = new Date(`${date}T00:00:00`);
    const label = isNaN(d.getTime())
      ? date
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    onSelect(date, label);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Search by Date" size="sm">
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-500">
          Show quotes whose departure or return date matches the selected day.
        </p>
        <div className="relative">
          <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!date}
            className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteSearchByDateModal;
